import { Amplify } from 'aws-amplify';
import {
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
} from 'aws-amplify/auth';
import { api, setIdToken, setIdTokenRefresher } from './api';
import { debugLog } from './debugLog';

let configured = false;

export type AuthProfile = {
  email: string;
  firstName: string;
  lastName: string;
  groups: string[];
  isUser: boolean;
  isAdmin: boolean;
};

const FALLBACK_FIRST = 'User';
const FALLBACK_LAST = 'Unknown';

function nameFromToken(payload: Record<string, unknown>): { firstName: string; lastName: string } {
  const first = String(payload.given_name || '').trim();
  const last = String(payload.family_name || '').trim();
  return {
    firstName: first || FALLBACK_FIRST,
    lastName: last || FALLBACK_LAST,
  };
}

export async function ensureAmplifyConfigured(): Promise<void> {
  if (configured) return;
  const cfg = await api.authConfig();
  if (!cfg.userPoolId || !cfg.userPoolClientId) {
    throw new Error('Cognito pool not configured on API');
  }
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: cfg.userPoolId,
        userPoolClientId: cfg.userPoolClientId,
        loginWith: { email: true },
      },
    },
  });
  configured = true;
}

export async function refreshToken(): Promise<string | null> {
  await ensureAmplifyConfigured();
  try {
    await getCurrentUser();
    const session = await fetchAuthSession({ forceRefresh: false });
    const token = session.tokens?.idToken?.toString() || null;
    setIdToken(token);
    return token;
  } catch {
    setIdToken(null);
    return null;
  }
}

/** Force Cognito refresh (used after API 401). */
export async function forceRefreshToken(): Promise<string | null> {
  await ensureAmplifyConfigured();
  try {
    await getCurrentUser();
    const session = await fetchAuthSession({ forceRefresh: true });
    const token = session.tokens?.idToken?.toString() || null;
    setIdToken(token);
    if (token) debugLog('auth.token_refreshed', {});
    else debugLog('auth.token_refresh_empty', {});
    return token;
  } catch (e) {
    setIdToken(null);
    debugLog('auth.token_refresh_failed', {
      message: e instanceof Error ? e.message : String(e),
    });
    return null;
  }
}

setIdTokenRefresher(forceRefreshToken);

export async function getAuthProfile(): Promise<AuthProfile | null> {
  const token = await refreshToken();
  if (!token) return null;
  const session = await fetchAuthSession();
  const payload = (session.tokens?.idToken?.payload || {}) as Record<string, unknown>;
  const groups = (payload['cognito:groups'] as string[]) || [];
  const { firstName, lastName } = nameFromToken(payload);
  return {
    email: String(payload.email || payload['cognito:username'] || ''),
    firstName,
    lastName,
    groups,
    isUser: groups.includes('user') || groups.includes('admin'),
    isAdmin: groups.includes('admin'),
  };
}

export async function userSignIn(email: string, password: string): Promise<AuthProfile> {
  await ensureAmplifyConfigured();
  await signIn({
    username: email,
    password,
    options: { authFlowType: 'USER_PASSWORD_AUTH' },
  });
  const profile = await getAuthProfile();
  if (!profile?.isUser) throw new Error('Signed in but missing user role');
  return profile;
}

export async function userSignOut(): Promise<void> {
  try {
    await ensureAmplifyConfigured();
    await signOut();
  } finally {
    setIdToken(null);
  }
}
